import React from 'react';

import { themeStore } from 'Models/ThemeStore';
import { CharterPaymentState } from 'Modules/Charter/Types/CharterScheduleItem';

/**
 * What a charter's money reads as, from the two things that decide it.
 *
 * Four labels out of three states, because **Invoiced** is not a payment state: it is
 * `Charter.IsInvoiced`, the mark that says accounting has been given the charter, laid over the
 * state the transaction is in. A charter can be awaiting payment and marked, or awaiting and not.
 *
 * Paid wins over the mark — once the money is in, that a copy went to accounting is no longer the
 * thing a reader wants from one word.
 *
 * Shared so the customer's tag and the staff charters table cannot describe one charter two
 * different ways; they read the same charter from the same two fields.
 */
export function charterPaymentLabel(
	state: CharterPaymentState | undefined,
	isInvoiced: boolean | null | undefined,
): string {
	if (state === 'Paid') {
		return 'Paid';
	}

	if (isInvoiced === true) {
		return 'Invoiced';
	}

	return state === 'Awaiting' ? 'Awaiting' : 'Unpaid';
}

/**
 * The colours that go with the label above, from the same two fields.
 *
 * Taken from `BookingStatusTag` case for case, including that awaiting and unpaid share the awaiting
 * background but not its text colour — unpaid pairs it with `textOnInvoicedTag` there. That reads
 * like a slip, but a charter tag that fixed it would sit beside booking tags that did not.
 */
export function charterPaymentTagStyles(
	state: CharterPaymentState | undefined,
	isInvoiced: boolean | null | undefined,
): React.CSSProperties {
	if (state === 'Paid') {
		return { background: themeStore.paidTagColour, color: themeStore.textOnPaidTag };
	}

	if (isInvoiced === true) {
		return { background: themeStore.invoicedTagColour, color: themeStore.textOnInvoicedTag };
	}

	if (state === 'Awaiting') {
		return { background: themeStore.awaitingTagColour, color: themeStore.textOnAwaitingTag };
	}

	return { background: themeStore.awaitingTagColour, color: themeStore.textOnInvoicedTag };
}
